import React, { useState } from 'react';
import moment from 'moment';
import swal from 'sweetalert';
import { useDispatch, useSelector } from 'react-redux';
import SvgImg1 from '../UI/SvgImg1';
import SvgImg1Hover from '../UI/SvgImg1Hover';
import SvgImg6 from '../UI/SvgImg6';
import SvgImg6Hover from '../UI/SvgImg6Hover';
import SvgImg7 from '../UI/SvgImg7';
import SvgImg7Hover from '../UI/SvgImg7Hover';
import Assessment from '../UI/Assessment';
import AssessmentHover from '../UI/AssessmentHover';
import PaymentOfTerminalCharges from '../UI/PaymentOfTerminalCharges';
import PaymentOfTerminalChargesHover from '../UI/PaymentOfTerminalChargesHover';
import CustomExamination from '../UI/CustomExamination';
import CustomExaminationHover from '../UI/CustomExaminationHover';
import CustomReleaseAndExitNote from '../UI/CustomReleaseAndExitNote';
import CustomReleaseAndExitNoteHover from '../UI/CustomReleaseAndExitNoteHover';
import PaymentOfShippingCharges from '../UI/PaymentOfShippingCharges';
import PaymentOfShippingChargesHover from '../UI/PaymentOfShippingChargesHover';
import { showModal } from '../../redux/error/ErrorAction';
import { POST } from '../../services/rest.service';
import { hideLoading, showLoading } from '../../redux/loader/LoaderActions';
import {
  DATE_FORMAT,
  TIME_FORMAT,
  DATE_TIME_FORMAT,
  USER_ROLE_CLEARING_AGENT,
  USER_ROLE_OPERATOR,
  USER_ROLE_APPLICATION_CLIENT,
  USER_ROLE_APPLICATION_CORPORATE_CLIENT,
} from '../../helpers/constants';

export const OWNER_AGENT = 'agent';
export const OWNER_OPERATOR = 'operator';

/**
 * The eight clearing milestones, in DISPLAY order (which differs from the key order
 * of Job::$clearingCargoStatus on the API — that one is only the storage template).
 *
 * `gate` is the milestone that must be recorded before this one becomes actionable;
 * null means always available. `owner` decides which role sees the toggle.
 *
 * IMPORTANT: these gating rules are mirrored server-side in
 * JobService::addClearingCargoStatus(). If you change them here, change them there
 * too, or a visible toggle will come back 403/422.
 *
 * `switchId` is carried explicitly because Payment of Customs Duty uses the bare
 * `myonoffswitch` id while the others are numerically suffixed — preserving them
 * keeps the existing CSS and DOM lookups working.
 *
 * `iconFirst` reproduces the alternating icon/label order that produces the zig-zag
 * timeline; do not "tidy" it into a single order.
 */
export const MILESTONES = [
  {
    key: 'assessment',
    switchId: 'myonoffswitch8',
    owner: OWNER_AGENT,
    gate: null,
    label: <span className="fw-bold pt-5">Assessment</span>,
    Icon: Assessment,
    IconHover: AssessmentHover,
    iconFirst: false,
  },
  {
    key: 'custom_duty_payment',
    switchId: 'myonoffswitch',
    owner: OWNER_OPERATOR,
    gate: 'assessment',
    label: (
      <span className="fw-bold">
        Payment of
        <br />
        Customs Duty
      </span>
    ),
    Icon: SvgImg1,
    IconHover: SvgImg1Hover,
    iconFirst: true,
  },
  {
    key: 'shipping_charges_payment',
    switchId: 'myonoffswitch5',
    owner: OWNER_AGENT,
    gate: 'custom_duty_payment',
    label: (
      <span className="fw-bold">
        Payment of
        <br />
        Shipping Charges
      </span>
    ),
    Icon: PaymentOfShippingCharges,
    IconHover: PaymentOfShippingChargesHover,
    iconFirst: false,
  },
  {
    key: 'terminal_charges_payment',
    switchId: 'myonoffswitch4',
    owner: OWNER_AGENT,
    gate: 'custom_duty_payment',
    label: (
      <span className="fw-bold">
        Payment of
        <br />
        Terminal Charges
      </span>
    ),
    Icon: PaymentOfTerminalCharges,
    IconHover: PaymentOfTerminalChargesHover,
    iconFirst: true,
    anchorClassName: 'ptc-svg',
  },
  {
    key: 'custom_examination',
    switchId: 'myonoffswitch2',
    owner: OWNER_AGENT,
    gate: 'custom_duty_payment',
    label: (
      <span className="fw-bold">
        Customs <br />
        Examination
      </span>
    ),
    Icon: CustomExamination,
    IconHover: CustomExaminationHover,
    iconFirst: false,
  },
  {
    key: 'custom_release',
    switchId: 'myonoffswitch3',
    owner: OWNER_AGENT,
    gate: 'custom_duty_payment',
    label: <span className="fw-bold">Customs Release and Exit Note</span>,
    Icon: CustomReleaseAndExitNote,
    IconHover: CustomReleaseAndExitNoteHover,
    iconFirst: true,
  },
  {
    key: 'terminal_release',
    switchId: 'myonoffswitch6',
    owner: OWNER_AGENT,
    gate: 'custom_duty_payment',
    label: (
      <span className="fw-bold">
        Terminal <br /> Release
      </span>
    ),
    Icon: SvgImg6,
    IconHover: SvgImg6Hover,
    iconFirst: false,
  },
  {
    key: 'custom_exit_gate',
    switchId: 'myonoffswitch7',
    owner: OWNER_AGENT,
    gate: 'custom_duty_payment',
    label: (
      <span className="fw-bold">
        Exit <br />
        Customs Gate
      </span>
    ),
    Icon: SvgImg7,
    IconHover: SvgImg7Hover,
    iconFirst: true,
    iconWrapperClassName: 'svg-img7',
  },
];

/**
 * Human label for a milestone key, used in the "waiting for..." hint.
 */
const milestoneTitle = (key) => {
  const found = MILESTONES.find((m) => m.key === key);
  if (!found) return key;
  // Flatten the JSX label back to plain text for the hint.
  const text = (node) => {
    if (typeof node === 'string') return node;
    if (Array.isArray(node)) return node.map(text).join(' ');
    if (node && node.props) return text(node.props.children);
    return '';
  };
  return text(found.label).replace(/\s+/g, ' ').trim();
};

const ClearingCargoStatus = ({ details, refresh, jobId }) => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.user);

  const [hoveringIcon, setHoveringIcon] = useState(null);

  const userIsClearingAgent = user.type === USER_ROLE_CLEARING_AGENT;
  const userIsClient =
    user.type === USER_ROLE_APPLICATION_CLIENT ||
    user.type === USER_ROLE_APPLICATION_CORPORATE_CLIENT;
  const userIsOperator = user.type === USER_ROLE_OPERATOR;

  const status = details.clearingCargoStatus || {};

  // The API refuses the agent's milestones until the importer has supplied the
  // Bill of Lading and TIN. Mirrored here so the toggles are hidden rather than
  // failing on click, and so the agent is told who they are waiting on.
  //
  // Jobs created before mode_of_release existed report complete, so nothing
  // already in flight shows this banner.
  const postAwardBlocked =
    details.privateJobDocs && details.privateJobDocs.post_award_docs_complete === false;

  const changesStatus = (key) => {
    dispatch(showLoading());
    POST(`/jobs/addClearingCargoStatus`, {
      job_uuid: jobId,
      [key]: moment().format(DATE_TIME_FORMAT),
    })
      .then(() => {
        dispatch(showModal('Job status changed successfully', true));
        if (refresh) refresh().then(() => dispatch(hideLoading()));
      })
      .catch((err) => {
        dispatch(hideLoading());
        dispatch(showModal(err.message));
      });
  };

  const changeStatusConfirm = (key, chkBoxId) => {
    swal({
      title: 'Are you sure?',
      text: `Are you sure you want to change job status?`,
      icon: 'warning',
      buttons: ['No', 'Yes'],
    }).then((result) => {
      const chkBox = document.getElementById(chkBoxId);
      if (result) {
        changesStatus(key);
        // Guard both branches — the "No" path previously assumed the element existed
        // and threw "Cannot read property 'checked' of null".
        if (chkBox) chkBox.checked = true;
      } else if (chkBox) {
        chkBox.checked = false;
      }
    });
  };

  return (
    // The strip is one connected chain of eight steps with arrows between them,
    // so it must not wrap — breaking it splits Exit Customs Gate off from the
    // rest. On narrow screens this wrapper scrolls horizontally instead.
    <div className="genaral-block-scroll">
      {postAwardBlocked && (userIsClearingAgent || userIsClient) && (
        <div className="alert alert-warning" style={{ marginBottom: '12px' }}>
          {userIsClearingAgent
            ? 'Waiting on the importer to supply the Bill of Lading and Tax Identification Number. Milestones cannot be recorded until then.'
            : 'Your clearing agent cannot record progress until you supply the Bill of Lading and Tax Identification Number for this job.'}
        </div>
      )}
      <ul className="genaral-block">
        {MILESTONES.map((m) => {
          const value = status[m.key];
          const gateMet = !m.gate || Boolean(status[m.gate]);
          const isOwner = m.owner === OWNER_AGENT ? userIsClearingAgent : userIsOperator;

          const { Icon, IconHover } = m;
          const showHover = Boolean(value) || hoveringIcon === m.key;
          let icon = showHover ? <IconHover /> : <Icon />;
          if (m.iconWrapperClassName) {
            icon = <p className={m.iconWrapperClassName}>{icon}</p>;
          }

          return (
            <li key={m.key}>
              <a
                onMouseEnter={() => setHoveringIcon(m.key)}
                onMouseLeave={() => setHoveringIcon(null)}
                className={`${m.anchorClassName ? m.anchorClassName + ' ' : ''}${
                  value ? 'active' : ''
                }`}
              >
                {m.iconFirst ? icon : m.label}
                {m.iconFirst ? m.label : icon}
              </a>

              {userIsClient && (
                <>
                  <br />
                  {value ? (
                    <span className="active1">Completed</span>
                  ) : (
                    <span>Not completed</span>
                  )}
                </>
              )}
              <br />
              {value && moment(value).format(DATE_FORMAT)}
              <br />
              {value && moment(value).format(TIME_FORMAT)}

              {/* The operator's Payment of Customs Duty stays available while
                  documents are outstanding — the block is on the agent's own
                  milestones only, so a job is never wedged with nobody able to
                  act. That mirrors the server-side guard exactly. */}
              {isOwner && gateMet && !(postAwardBlocked && m.owner === OWNER_AGENT) && (
                <div className="onoffswitch">
                  <input
                    type="checkbox"
                    name="onoffswitch"
                    className="onoffswitch-checkbox"
                    id={m.switchId}
                    disabled={value ? true : false}
                    onChange={() => {
                      changeStatusConfirm(m.key, m.switchId);
                    }}
                    defaultChecked={value ? true : false}
                  />
                  <label className="onoffswitch-label" htmlFor={m.switchId}>
                    <span className="onoffswitch-inner"></span>
                    <span className="onoffswitch-switch"></span>
                  </label>
                </div>
              )}

              {/* Without this the toggle just silently disappears, which is confusing
                  now that several milestones unlock at the same time. */}
              {isOwner && !gateMet && (
                <small className="text-muted d-block mt-1">
                  Awaiting {milestoneTitle(m.gate)}
                </small>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default ClearingCargoStatus;
